import QRCode from 'qrcode';
import opentype from 'opentype.js';

// SVG dimensions (must match reference Frame 206.svg exactly)
const SVG_WIDTH = 325;
const SVG_HEIGHT = 326;

// Border (thin outline at the edge; inset = half stroke width so outer edge aligns with SVG boundary)
const BORDER_INSET = 0.136125;
const BORDER_WIDTH = 324.728;
const BORDER_HEIGHT = 325.728;
const BORDER_STROKE = 0.27225;

// Text baselines (y) and font sizes — text is horizontally centred on the QR code
const FIRST_NAME_Y = 195.5;
const FIRST_NAME_FONT_SIZE = 33;

const LAST_NAME_Y = 217;
const LAST_NAME_FONT_SIZE = 23;

// QR code position and size
const QR_X = 158;
const QR_Y = 251.474;
const QR_SIZE = 55;

// Horizontal centre axis for text (= centre of QR code)
const TEXT_CENTER_X = QR_X + QR_SIZE / 2; // 185.5

const TEXT_COLOR = '#000000';

// ── Font loading (cached after first call) ──────────────────────────────────
let fontPromise = null;

function loadFont() {
  if (!fontPromise) {
    fontPromise = opentype.load('/fonts/BalooPaaji2-Bold.ttf').catch(
      (err) => {
        console.error('Failed to load Baloo Paaji 2 Bold font:', err);
        fontPromise = null; // allow retry on next call
        throw err;
      }
    );
  }
  return fontPromise;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Convert a text string to an SVG <path> d-attribute using opentype.js,
 * horizontally centred at `centerX`.
 * Returns the path data string, or null on failure.
 */
function textToPathData(font, text, centerX, y, fontSize) {
  if (!text) return null;
  const textWidth = font.getAdvanceWidth(text, fontSize);
  const x = centerX - textWidth / 2;
  const path = font.getPath(text, x, y, fontSize);
  return path.toPathData(3); // 3 decimal places
}

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * Generates an SVG string for a laser-cut badge.
 * All text is converted to vector outlines (no font dependency in the SVG).
 * QR code is rendered as SVG rects.
 *
 * Layout matches the reference design (325×326) with:
 *   - Thin border outline
 *   - First name in 33-size gold vector outlines
 *   - Last name in 23-size gold vector outlines
 *   - QR code (55×55) rendered as individual SVG rects
 *
 * @param {string} firstName
 * @param {string} lastName
 * @param {string} [qrUrl] - URL to encode as QR. Omit or empty for no QR code.
 * @returns {Promise<string>} The SVG markup (all vectors, no text elements)
 */
export async function generateLaserBadgeSVG(firstName, lastName, qrUrl) {
  const font = await loadFont();

  // ── Text → vector paths (centred on QR code) ───────────────────────────
  const firstNamePath = textToPathData(
    font,
    firstName,
    TEXT_CENTER_X,
    FIRST_NAME_Y,
    FIRST_NAME_FONT_SIZE
  );

  const lastNamePath = textToPathData(
    font,
    lastName,
    TEXT_CENTER_X,
    LAST_NAME_Y,
    LAST_NAME_FONT_SIZE
  );

  // ── QR code → SVG rects ───────────────────────────────────────────────
  let qrElements = '';

  if (qrUrl && qrUrl.trim()) {
    try {
      const qr = QRCode.create(qrUrl, { errorCorrectionLevel: 'M' });
      const { size: moduleCount, data } = qr.modules;
      const moduleSize = QR_SIZE / moduleCount;

      for (let row = 0; row < moduleCount; row++) {
        for (let col = 0; col < moduleCount; col++) {
          if (data[row * moduleCount + col]) {
            const x = QR_X + col * moduleSize;
            const y = QR_Y + row * moduleSize;
            qrElements += `<rect x="${x.toFixed(3)}" y="${y.toFixed(3)}" width="${moduleSize.toFixed(3)}" height="${moduleSize.toFixed(3)}" fill="black"/>\n`;
          }
        }
      }
    } catch (err) {
      console.error('Failed to generate QR code for laser badge:', err);
    }
  }

  // ── Assemble SVG ──────────────────────────────────────────────────────
  return `<svg width="${SVG_WIDTH}" height="${SVG_HEIGHT}" viewBox="0 0 ${SVG_WIDTH} ${SVG_HEIGHT}" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect x="${BORDER_INSET}" y="${BORDER_INSET}" width="${BORDER_WIDTH}" height="${BORDER_HEIGHT}" stroke="black" stroke-width="${BORDER_STROKE}"/>
${firstNamePath ? `<path d="${firstNamePath}" fill="${TEXT_COLOR}"/>` : ''}
${lastNamePath ? `<path d="${lastNamePath}" fill="${TEXT_COLOR}"/>` : ''}
${qrElements}</svg>`;
}

/**
 * Triggers a browser download of an SVG string as a .svg file.
 *
 * @param {string} svgString - The SVG markup
 * @param {string} fileName  - e.g. "badge-jane-doe.svg"
 */
export function downloadSVG(svgString, fileName) {
  const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
